import { CurvePathData } from '@elfalem/leaflet-curve';
import { Marker } from 'leaflet';
import { IconHelper } from 'src/app/core/leaflet-helper/icon-helper';
import { AssetStatus } from 'src/app/maps/domain/livedata/status.enum';
import { MapElement } from 'src/app/maps/domain/masterdata/map-element.model';
import { SisMapAssetCategory } from 'src/app/maps/domain/masterdata/sismap-asset-category.enum';
import { CurveEndMarkers } from 'src/app/maps/map/style/curve-end-markers.model';
import { IconColor } from 'src/app/maps/map/style/icon-color.model';
import { MapStyle } from 'src/app/maps/map/style/styles/map-style.base';
import { environment } from 'src/environments/environment';

export class MapStyleSisag extends MapStyle {
  readonly name = 'sisag';

  private readonly urlBlobPath = 'icons/sismedia/styles/sisag/';

  constructor(protected iconHelper: IconHelper) {
    super(iconHelper);
  }

  async getIconSvg(iconName: string): Promise<string> {
    const url = this.urlBlobPath + iconName + '.svg';

    return this.iconHelper.getIcon(url);
  }

  getLayerIconSvgUrl(layerIconName: string, alternateStyle: boolean): string {
    void alternateStyle;
    return `${environment.baseUrlPublicAssets}/sismap/layer-selector-icons/${layerIconName}.svg`;
  }

  getSeasonIconSvgUrl(layerIconName: string, alternateStyle: boolean): string {
    void layerIconName;
    void alternateStyle;
    return '';
  }

  createCurveEndMarkers(curveCoords: CurvePathData, color: string, element: MapElement, pane: string): CurveEndMarkers {
    void curveCoords;
    void color;
    void element;
    void pane;
    return { visible: [], highlight: [] };
  }

  updateCurveEndMarkers(
    curveCoords: CurvePathData,
    curveEndMarkers: CurveEndMarkers,
    color: string,
    element: MapElement,
  ): void {
    void curveCoords;
    void curveEndMarkers;
    void color;
    void element;
  }

  zoomCurveEndMarkers(curveEndMarkers: CurveEndMarkers, scaling: number): void {
    void curveEndMarkers;
    void scaling;
  }

  setHighlight(curveEndMarkers: CurveEndMarkers, highlight: boolean): void {
    void curveEndMarkers;
    void highlight;
  }

  getSlopeColor(element: MapElement, closed: boolean): IconColor {
    if (closed) {
      return { statusColor: '#808080', textColor: '#ffffff' };
    }

    const pathColor = element.pathColor ?? element.defaultPathColor;
    const textColor = element.isCategorySki ? this.calculateTextColor(pathColor) : '#ffffff';
    return { statusColor: pathColor, textColor };
  }

  calculateTextColor(pathColor: string): string {
    const normalizeHex = (hex: string): string => {
      let h = hex.trim().replace(/^#/, '');
      if (h.length === 3) {
        h = h
          .split('')
          .map((c) => c + c)
          .join('');
      }
      if (!/^[0-9a-fA-F]{6}$/.test(h)) {
        return '000000';
      }
      return h.toLowerCase();
    };

    const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
      const h = normalizeHex(hex);
      return {
        r: parseInt(h.slice(0, 2), 16),
        g: parseInt(h.slice(2, 4), 16),
        b: parseInt(h.slice(4, 6), 16),
      };
    };

    const relativeLuminance = ({ r, g, b }: { r: number; g: number; b: number }): number => {
      const srgb = [r, g, b].map((v) => v / 255);
      const lin = srgb.map((c) => (c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));
      return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
    };

    const lum = relativeLuminance(hexToRgb(pathColor));
    const isLight = lum > 0.5;

    return isLight ? '#2b2b2b' : '#ffffff';
  }

  getLiftColor(assetStatus: AssetStatus | undefined, element: MapElement, overrideColor = false): IconColor {
    if (element.pathColor != null && !overrideColor) {
      return { statusColor: element.pathColor, textColor: '#ffffff' };
    }

    switch (assetStatus) {
      case AssetStatus.Open:
        return { statusColor: '#1dab38', textColor: '#ffffff' };
      case AssetStatus.Closed:
        return { statusColor: '#af272f', textColor: '#ffffff' };
      case AssetStatus.Preparation:
        return { statusColor: '#e68523', textColor: '#ffffff' };
      case AssetStatus.Disabled:
        return { statusColor: '#555555', textColor: '#ffffff' };
      case AssetStatus.NoStatus:
        return { statusColor: '#555555', textColor: '#ffffff' };
      default:
        return { statusColor: '#555555', textColor: '#ffffff' };
    }
  }

  getColor(element: MapElement, useStatusColor = false): IconColor {
    if (element.isCategorySki && !useStatusColor) {
      return this.getSlopeColor(element, element.isGrey);
    }

    const status = element.status;

    switch (status) {
      case AssetStatus.Open:
        return { statusColor: '#1dab38', textColor: '#ffffff' };
      case AssetStatus.Closed:
        return { statusColor: '#af272f', textColor: '#ffffff' };
      case AssetStatus.Preparation:
        return { statusColor: '#e68523', textColor: '#ffffff' };
      case AssetStatus.Disabled:
        return { statusColor: '#ffffff', textColor: '#000000' };
      case AssetStatus.NoStatus:
        return { statusColor: '#ffffff', textColor: '#000000' };
      default:
        return { statusColor: '#ffffff', textColor: '#000000' };
    }
  }

  setIconColor(icon: Marker, backgroundColor: string, symbolColor?: string): void {
    const e = icon.getElement()?.querySelector('#background');
    e?.setAttribute('fill', backgroundColor);

    if (symbolColor) {
      const s = icon.getElement()?.querySelector('#symbol');
      s?.setAttribute('fill', symbolColor);
    }
  }

  setSidepaneIconColor(iconSvg: string, backgroundColor: string, symbolColor?: string): string {
    iconSvg = this.setSvgColor('background', backgroundColor, iconSvg);
    if (symbolColor) {
      iconSvg = this.setSvgColor('symbol', symbolColor, iconSvg);
    }
    return iconSvg;
  }

  private setSvgColor(id: string, color: string, iconSvg: string): string {
    const regex = new RegExp(`(id="${id}"[^>]*?)fill="[^"]*"`);
    if (iconSvg.match(regex)) {
      iconSvg = iconSvg.replace(regex, `$1fill="${color}"`);
    } else {
      iconSvg = iconSvg.replace(`id="${id}"`, `id="${id}" fill="${color}"`);
    }
    return iconSvg;
  }

  setIconBorder(icon: Marker): void {
    const element = icon.getElement();

    if (!element) {
      return;
    }

    const background = element.querySelector('#background') ?? element.querySelectorAll('svg rect')[0];

    if (background) {
      background.setAttribute('stroke', '#808080');
      background.setAttribute('stroke-width', '1');
      background.setAttribute('stroke-opacity', '0.8');
      background.setAttribute('rx', '10px');
      background.setAttribute('ry', '10px');
    }
  }

  async createPopupContent(
    element: MapElement,
    showLabel: boolean,
  ): Promise<{ popupContent: string; className: string }> {
    const colors =
      element.category === SisMapAssetCategory.Lift
        ? this.getLiftColor(element.status, element, true)
        : this.getColor(element, showLabel);

    const svg = element.type ? await this.getIconSvg(element.type).catch(() => '') : '';
    let iconHtml = '';
    if (svg) {
      const parser = new DOMParser();
      const icon = parser.parseFromString(svg, 'image/svg+xml');
      if (!icon.querySelector('parsererror')) {
        const e = icon.querySelector('#background');
        e?.setAttribute('fill', colors.statusColor);
        const s = icon.querySelector('#symbol');
        s?.setAttribute('fill', colors.textColor);
        iconHtml = icon.documentElement.outerHTML;
      }
    }

    const label = showLabel && element.label ? `${element.label} ` : '';
    const popupContent = `<span>${label}${element.name}</span>${iconHtml}`;
    const className = `sis-map-tooltip-${this.name}`;

    return { popupContent, className };
  }

  getSignPostSvgUrl(): string {
    return `${this.urlBlobPath}signpost.svg`;
  }
}
